import { ListFilter } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type TagFilterPopoverProps = {
  tags: string[]
  // Empty selection means "Alle": every course is shown, including untagged ones.
  selectedTags: ReadonlySet<string>
  onChange: (selectedTags: Set<string>) => void
  isLoading?: boolean
  className?: string
}

export function TagFilterPopover({
  tags,
  selectedTags,
  onChange,
  isLoading = false,
  className,
}: TagFilterPopoverProps) {
  const isFiltered = selectedTags.size > 0

  function toggleTag(tag: string, checked: boolean) {
    const next = new Set(selectedTags)
    if (checked) {
      next.add(tag)
    } else {
      next.delete(tag)
    }
    onChange(next)
  }

  return (
    <div className={cn('flex justify-center', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 gap-1.5 rounded-full px-3 py-0 font-nav text-[11px] font-bold uppercase tracking-wider',
              isFiltered
                ? 'bg-primary/30 text-primary hover:bg-primary/35 hover:text-primary'
                : 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
            )}
            aria-label={
              isFiltered
                ? `Tagfilter aktiv: ${selectedTags.size} ${selectedTags.size === 1 ? 'Tag' : 'Tags'} ausgewählt`
                : 'Kurse nach Tags filtern'
            }
          >
            <ListFilter aria-hidden="true" className="h-3.5 w-3.5" />
            Filter
            {isFiltered && <span className="tabular-nums">{selectedTags.size}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          sideOffset={8}
          className="w-[min(19rem,calc(100vw-2rem))] p-3"
        >
          <p className="font-nav text-sm font-semibold text-foreground">Kurse nach Tags filtern</p>

          {isLoading ? (
            <p className="mt-2 text-sm text-muted-foreground">Wird geladen …</p>
          ) : (
            <ul className="mt-2 max-h-72 space-y-0.5 overflow-y-auto">
              <li>
                <label
                  htmlFor="tag-filter-all"
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60"
                >
                  <Checkbox
                    id="tag-filter-all"
                    checked={!isFiltered}
                    onCheckedChange={(value) => {
                      if (value === true) onChange(new Set())
                    }}
                  />
                  <span className={cn('font-medium', isFiltered ? 'text-muted-foreground' : 'text-foreground')}>
                    Alle
                  </span>
                </label>
              </li>
              {tags.map((tag, index) => {
                const checked = selectedTags.has(tag)
                const inputId = `tag-filter-${index}`

                return (
                  <li key={tag}>
                    <label
                      htmlFor={inputId}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60"
                    >
                      <Checkbox
                        id={inputId}
                        checked={checked}
                        onCheckedChange={(value) => toggleTag(tag, value === true)}
                      />
                      <span
                        className={cn(
                          'min-w-0 truncate',
                          checked ? 'text-foreground' : 'text-muted-foreground',
                        )}
                      >
                        {tag}
                      </span>
                    </label>
                  </li>
                )
              })}
              {!tags.length && (
                <li className="px-2 py-1.5 text-sm text-muted-foreground">Keine Tags vorhanden.</li>
              )}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
